import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm } from 'antd';
import api from '../../../utils/api';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const EmailTemplates = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const loadData = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const resp = await api.get(`/email-templates?limit=${pageSize}&offset=${(page-1)*pageSize}`);
      setData(resp.items || []);
      setPagination({ current: page, pageSize });
    } catch (e) {
      message.error(e.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'Key', dataIndex: 'key', key: 'key', width: 200 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Type', dataIndex: 'type', key: 'type', width: 160 },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" type="link" onClick={() => onEdit(record)}>Edit</Button>
          {record.enabled ? (
            <Button size="small" danger onClick={() => onDisable(record)}>Disable</Button>
          ) : (
            <Button size="small" onClick={() => onEnable(record)}>Enable</Button>
          )}
          <Popconfirm title="Delete this template?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => onDelete(record)}>
            <Button size="small" danger type="link">Delete</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => { loadData(1, pagination.pageSize); }, [pagination.pageSize]);

  const onCreate = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const onEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setOpen(true);
  };

  const onDisable = async (record) => {
    try {
      await api.put(`/email-templates/${record.id}`, { enabled: false });
      message.success(`Disabled template ${record.name}`);
      loadData(pagination.current, pagination.pageSize);
    } catch (e) {
      if (String(e.message || '').includes('401')) message.error('Session expired. Please log in.');
      else message.error(e.message || 'Failed to disable');
    }
  };

  const onEnable = async (record) => {
    try {
      await api.put(`/email-templates/${record.id}`, { enabled: true });
      message.success(`Enabled template ${record.name}`);
      loadData(pagination.current, pagination.pageSize);
    } catch (e) {
      if (String(e.message || '').includes('401')) message.error('Session expired. Please log in.');
      else message.error(e.message || 'Failed to enable');
    }
  };

  const onDelete = async (record) => {
    try {
      await api.delete(`/email-templates/${record.id}`);
      message.success('Template deleted');
      loadData(pagination.current, pagination.pageSize);
    } catch (e) {
      if (String(e.message || '').includes('401')) message.error('Session expired. Please log in.');
      else message.error(e.message || 'Failed to delete');
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        await api.put(`/email-templates/${editing.id}`, values);
        message.success('Template updated');
      } else {
        await api.post('/email-templates', values);
        message.success('Template created');
      }
      setOpen(false);
      setEditing(null);
      loadData(pagination.current, pagination.pageSize);
    } catch (e) {
      if (e?.errorFields) return;
      if (String(e.message || '').includes('401')) message.error('Session expired. Please log in.');
      else message.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[
        { title: 'Admin' },
        { title: 'Settings' },
        { title: 'Email & Notification Templates' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Email & Notification Templates</Title>
        <Space>
          <Button type="primary" onClick={onCreate}>New Template</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Manage email and notification templates. Replace with your settings endpoints.
        </Paragraph>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{ current: pagination.current, pageSize: pagination.pageSize, onChange: loadData }}
        />
      </Card>

      <Modal
        open={open}
        title={editing ? 'Edit Template' : 'Create Template'}
        onCancel={() => { setOpen(false); setEditing(null); }}
        onOk={onSubmit}
        confirmLoading={saving}
      >
        <Form layout="vertical" form={form}>
          <Form.Item label="Key" name="key" rules={[{ required: true }]}>
            <Input placeholder="e.g., welcome_email" disabled={!!editing} />
          </Form.Item>
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Type" name="type" rules={[{ required: true }]} initialValue={editing?.type || 'email'}>
            <Select>
              <Option value="email">Email</Option>
              <Option value="notification">Notification</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Subject" name="subject" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Body" name="body" rules={[{ required: true }]}>
            <Input.TextArea rows={6} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmailTemplates;
